import React, { useState, useEffect, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ChevronDownIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import BestSupplierResultsTable from './BestSupplier_ResultsTable';
import { mockPrestations } from './data/mockData';
import { useCalculations } from './hooks/useCalculations';

const MeilleurFournisseur: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('ascenseurs');
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>(['A', 'B', 'C']);
  const [isSupplierDropdownOpen, setIsSupplierDropdownOpen] = useState(false);
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);
  const [quantites, setQuantites] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    mockPrestations.forEach((p) => {
      initial[p.id] = p.quantite;
    });
    return initial;
  });
  const supplierDropdownRef = useRef<HTMLDivElement>(null);

  const domainOptions = ['ascenseurs', 'extincteurs'];
  const supplierOptions = ['A', 'B', 'C'];

  const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => {
    return (
      <div className="relative inline-block">
        <div
          onMouseEnter={() => setHoveredTooltip(text)}
          onMouseLeave={() => setHoveredTooltip(null)}
        >
          {children}
        </div>
        {hoveredTooltip === text && (
          <div className="absolute z-50 w-64 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-normal">
            {text}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
              <div className="border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleSupplierToggle = (supplier: string) => {
    setSelectedSuppliers(prev =>
      prev.includes(supplier)
        ? prev.filter(s => s !== supplier)
        : [...prev, supplier]
    );
  };

  // Calculs des métriques
  const {
    economieRealisee,
    ecartsTypesPrestations,
    ecartsTypesFournisseurs,
    ecartsTypesPotentiels,
  } = useCalculations({
    prestations: mockPrestations,
    selectedSuppliers,
    quantites,
  });

  // Fonction pour formater les prix en français
  const formatPrix = (prix: number) => {
    return prix.toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target as Node)) {
        setIsSupplierDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Section 1: Header */}
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-violet-700 leading-tight">Meilleur fournisseur</h1>
          <p className="mt-2 text-base text-gray-600">
            Comparez instantanément le prix catalogue de vos fournisseurs pour partir du meilleur prix
            négociés.
          </p>
        </header>

        {/* Section 2: User manipulation zone */}
        <section className="p-6 bg-white shadow rounded-xl mb-8">
          {/* Top row: Domain filter, Import and Export buttons */}
          <div className="flex items-end gap-4 mb-4">
            <div className="flex-1 md:flex-none">
              <label className="block text-sm font-medium text-gray-700 mb-2">Domaine technique *</label>
              <select
                value={selectedDomain}
                onChange={(e) => setSelectedDomain(e.target.value)}
                className="w-full md:w-64 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                {domainOptions.map(domain => (
                  <option key={domain} value={domain}>
                    {domain.charAt(0).toUpperCase() + domain.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled
            >
              <ArrowUpTrayIcon className="w-5 h-5" />
              <span>Import</span>
            </button>
            <button
              className="ml-auto flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              <span>Export</span>
            </button>
          </div>

          {/* Search bar and Supplier filter */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
              <div className="relative mt-2">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                />
                {searchTerm && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-2 h-2 bg-violet-500 rounded-full animate-pulse"></div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Supplier filter - multi-select dropdown */}
            <div className="relative w-64" ref={supplierDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fournisseur</label>
              <button
                type="button"
                onClick={() => setIsSupplierDropdownOpen(!isSupplierDropdownOpen)}
                className="flex items-center justify-between w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <span className="text-gray-700">
                  {selectedSuppliers.length === 0
                    ? 'Sélectionner'
                    : selectedSuppliers.length === supplierOptions.length
                    ? 'Tous sélectionnés'
                    : `${selectedSuppliers.length} sélectionné(s)`}
                </span>
                <ChevronDownIcon
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    isSupplierDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              {isSupplierDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                  <div className="py-1 max-h-60 overflow-auto">
                    {supplierOptions.map(supplier => (
                      <label
                        key={supplier}
                        className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedSuppliers.includes(supplier)}
                          onChange={() => handleSupplierToggle(supplier)}
                          className="mr-3 w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
                        />
                        <span className="text-sm text-gray-700">{supplier}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Section 3: Results zone */}
        <section className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-violet-600">
              <span>Économie réalisée</span>
              <Tooltip text="Différence entre la somme des totaux MAX et la somme des totaux MIN pour toutes les prestations">
                <InformationCircleIcon className="h-4 w-4 text-gray-400 cursor-help" />
              </Tooltip>
            </div>
            <div className="text-3xl font-semibold text-gray-900">
              {formatPrix(economieRealisee)} €
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-lg border border-gray-50 bg-gray-50 px-4 py-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
                <span>Écarts types prestations</span>
                <Tooltip text="Somme des écarts en valeur de toutes les lignes de prestations qui existent dans tous les fournisseurs sélectionnés">
                  <InformationCircleIcon className="h-4 w-4 text-gray-400 cursor-help" />
                </Tooltip>
              </div>
              <p className="mt-2 text-xl font-semibold text-gray-900">
                {formatPrix(ecartsTypesPrestations)} €
              </p>
            </div>
            <div className="rounded-lg border border-gray-50 bg-gray-50 px-4 py-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
                <span>Écart-types fournisseurs</span>
                <Tooltip text="Écart minimum et maximum entre les fournisseurs sur les prestations où tous les fournisseurs sélectionnés ont proposé un prix">
                  <InformationCircleIcon className="h-4 w-4 text-gray-400 cursor-help" />
                </Tooltip>
              </div>
              <p className="mt-2 text-xl font-semibold text-gray-900">
                {ecartsTypesFournisseurs.min === ecartsTypesFournisseurs.max
                  ? formatPrix(ecartsTypesFournisseurs.min)
                  : `${formatPrix(ecartsTypesFournisseurs.min)} - ${formatPrix(ecartsTypesFournisseurs.max)}`}{' '}
                €
              </p>
            </div>
            <div className="rounded-lg border border-gray-50 bg-gray-50 px-4 py-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
                <span>Écart-types potentiels</span>
                <Tooltip text="Écart minimum et maximum possible sur les prestations où au moins un fournisseur a proposé un prix">
                  <InformationCircleIcon className="h-4 w-4 text-gray-400 cursor-help" />
                </Tooltip>
              </div>
              <p className="mt-2 text-xl font-semibold text-gray-900">
                {ecartsTypesPotentiels.min === ecartsTypesPotentiels.max
                  ? formatPrix(ecartsTypesPotentiels.min)
                  : `${formatPrix(ecartsTypesPotentiels.min)} - ${formatPrix(ecartsTypesPotentiels.max)}`}{' '}
                €
              </p>
            </div>
          </div>
        </section>

        {/* Section 4: Results Table */}
        <section className="mt-8">
          <BestSupplierResultsTable
            prestations={mockPrestations}
            selectedSuppliers={selectedSuppliers}
            searchTerm={searchTerm}
            onQuantitesChange={setQuantites}
          />
        </section>
      </div>
    </div>
  );
};

export default MeilleurFournisseur;

